/**
 * A minimal table, because every admin list needs one and the SPA had none.
 *
 * Deliberately dumb: no sorting, no selection, no virtualisation. The admin
 * lists are all server-paginated by cursor, so client-side sort would sort ONE
 * page and present it as if it were the ordering of the whole set — which is
 * worse than no sort at all, because it looks like it worked.
 *
 * Numeric cells should carry `tabular-nums`; `Td.num` does it for you. Money
 * columns that jitter in width as digits change are hard to scan down, which is
 * the only thing anyone does with a money column.
 */
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';

export function Table({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`overflow-x-auto rounded-xl border border-white/[0.08] ${className}`}>
      <table className="w-full min-w-[640px] border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Thead({ children }: { children: ReactNode }) {
  return <thead className="bg-white/[0.03]">{children}</thead>;
}

export function Tbody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-white/[0.05]">{children}</tbody>;
}

export function Tr({
  children,
  onClick,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={`${onClick ? 'cursor-pointer hover:bg-white/[0.04]' : ''} transition ${className}`}
    >
      {children}
    </tr>
  );
}

export function Th({
  children,
  className = '',
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }) {
  return (
    <th
      className={`px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-white/40 whitespace-nowrap ${className}`}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  num = false,
  className = '',
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode; num?: boolean }) {
  return (
    <td
      className={`px-3 py-2.5 text-white/80 align-middle ${num ? 'tabular-nums text-right' : ''} ${className}`}
      {...props}
    >
      {children}
    </td>
  );
}

/** Full-width empty state. `colSpan` must match the header or the row collapses. */
export function TableEmpty({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-10 text-center text-sm text-white/35">
        {children}
      </td>
    </tr>
  );
}
